import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { lineDetails } from "../lib/cart";
import { Button } from "../components/ui/button";
import { CheckCircle2, Clock, ChefHat, Truck, PackageCheck, Smartphone, CreditCard, MapPin, Phone, ArrowLeft, XCircle } from "lucide-react";

const STATUS_FLOW = [
  { key: "received", label: "Order Received", icon: Clock },
  { key: "preparing", label: "Preparing", icon: ChefHat },
  { key: "out_for_delivery", label: "Out for Delivery", icon: Truck },
  { key: "delivered", label: "Delivered", icon: PackageCheck },
];

export default function OrderConfirmation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);
  const paidOnceRef = useRef(false);

  const load = async () => {
    try { const r = await api.get(`/orders/${id}`); setOrder(r.data); } catch { navigate("/"); }
  };

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [id]);

  // Auto trigger simulated STK push for fresh orders
  const simulatePay = () => {
    setPaying(true);
    const delay = order.payment_method === "mpesa" ? 2600 : 1600;
    setTimeout(async () => {
      try { await api.post(`/orders/${id}/pay`); await load(); } finally { setPaying(false); }
    }, delay);
  };

  useEffect(() => {
    const fresh = location.state?.freshOrder;
    if (fresh && order && order.payment_status === "pending" && !paidOnceRef.current) {
      paidOnceRef.current = true;
      navigate(location.pathname, { replace: true, state: null });
      simulatePay();
    }
  }, [order, location.state, id]);

  if (!order) return (
    <div className="min-h-screen grid place-items-center text-gray-500">Loading your order…</div>
  );

  const activeIdx = STATUS_FLOW.findIndex((s) => s.key === order.status);
  const isMpesa = order.payment_method === "mpesa";

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="mlobox-gradient text-white sticky top-0 z-20 shadow">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button data-testid="back-home" onClick={() => navigate("/")} className="rounded-full bg-white/15 hover:bg-white/25 p-2">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="font-display font-extrabold text-lg">Order {order.code}</div>
            <div className="text-xs text-white/80">Thanks, {order.customer_name.split(" ")[0]}!</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {order.status === "cancelled" ? (
          <section data-testid="order-cancelled-card" className="bg-white rounded-2xl border border-red-100 p-6 text-center">
            <div className="inline-grid place-items-center h-16 w-16 rounded-full bg-red-50 text-red-600"><XCircle size={30} /></div>
            <h2 className="font-display font-bold text-xl mt-3">This order was cancelled</h2>
            <p className="text-gray-500 text-sm mt-1">Please contact us if you have questions about this order.</p>
          </section>
        ) : order.payment_status === "pending" ? (
          <section data-testid="payment-pending-card" className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
            <div className={`inline-grid place-items-center h-16 w-16 rounded-full ${isMpesa ? "bg-[#00A859]/10 text-[#00A859] mpesa-pulse" : "bg-[#CC1A00]/10 text-[#CC1A00]"}`}>
              {isMpesa ? <Smartphone size={28} /> : <CreditCard size={28} />}
            </div>
            <h2 className="font-display font-bold text-xl mt-4">
              {isMpesa ? "Awaiting M-Pesa confirmation" : "Processing card payment"}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {isMpesa
                ? `We've sent an STK push to ${order.phone}. Enter your M-Pesa PIN to confirm KSh ${order.total}.`
                : `Securely charging your card KSh ${order.total}…`}
            </p>
            {paying ? (
              <div data-testid="payment-waiting" className="text-xs text-gray-400 mt-3">Waiting for confirmation… just a moment.</div>
            ) : (
              <Button data-testid="confirm-payment-btn" onClick={simulatePay} className="mt-4 rounded-full mlobox-gradient text-white font-semibold">
                {isMpesa ? "Resend prompt & confirm" : "Retry payment"}
              </Button>
            )}
          </section>
        ) : (
          <section data-testid="payment-success-card" className="bg-white rounded-2xl border border-gray-100 p-6 text-center animate-fade-up">
            <div className="inline-grid place-items-center h-16 w-16 rounded-full bg-[#059669]/10 text-[#059669]">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="font-display font-bold text-2xl mt-3">Payment confirmed!</h2>
            <p className="text-gray-500 text-sm mt-1">Ref <span className="font-mono text-gray-700">{order.payment_ref}</span></p>
          </section>
        )}

        {/* Order tracker */}
        {order.status !== "cancelled" && <section className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-display font-bold mb-4">Delivery status</h3>
          <ol data-testid="order-tracker" className="relative flex sm:items-start justify-between sm:gap-0 overflow-x-auto">
            {STATUS_FLOW.map((s, i) => {
              const Icon = s.icon;
              const done = i <= activeIdx;
              const current = i === activeIdx;
              return (
                <li key={s.key} className="flex-1 min-w-0 flex flex-col items-center text-center relative">
                  <div className={`h-10 w-10 rounded-full grid place-items-center z-10 ${done ? "mlobox-gradient text-white shadow" : "bg-gray-100 text-gray-400"} ${current ? "ring-4 ring-[#FFD1C0]" : ""}`}>
                    <Icon size={18} />
                  </div>
                  <div className={`text-[11px] mt-2 font-medium ${done ? "text-gray-900" : "text-gray-400"}`}>{s.label}</div>
                  {i < STATUS_FLOW.length - 1 && (
                    <div className={`absolute top-5 left-1/2 w-full h-0.5 ${i < activeIdx ? "mlobox-gradient" : "bg-gray-200"}`}></div>
                  )}
                </li>
              );
            })}
          </ol>
          <div className="mt-5 text-sm text-gray-500 text-center">
            Estimated delivery <span className="font-semibold text-gray-900">{order.eta_min}-{order.eta_max} minutes</span>
          </div>
        </section>}

        {/* Order details */}
        <section className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
          <h3 className="font-display font-bold">Order summary</h3>
          <div className="space-y-1 text-sm">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex justify-between gap-3">
                <span className="text-gray-700">{i.quantity}× {i.name}{lineDetails(i) && <span className="block text-[11px] text-gray-500">{lineDetails(i)}</span>}</span>
                <span className="font-semibold">KSh {i.price * i.quantity}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 pt-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>KSh {order.subtotal}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-emerald-700"><span>{order.reward?.label || "Discount"}</span><span>- KSh {order.discount}</span></div>}
            <div className="flex justify-between"><span className="text-gray-500">Delivery fee</span><span>KSh {order.delivery_fee}</span></div>
            <div className="flex justify-between pt-2 border-t border-gray-100 font-bold"><span>Total</span><span className="text-[#CC1A00]">KSh {order.total}</span></div>
          </div>
          <div className="pt-4 border-t border-gray-100 text-sm grid gap-2">
            <div className="flex items-center gap-2 text-gray-600"><MapPin size={14} /> {order.address}</div>
            <div className="flex items-center gap-2 text-gray-600"><Phone size={14} /> {order.phone}</div>
          </div>
        </section>

        <Button onClick={() => navigate("/")} className="w-full rounded-full py-6 mlobox-gradient text-white font-semibold" data-testid="order-again-btn">
          Order again
        </Button>
      </main>
    </div>
  );
}
